import { useCallback, useState } from 'react';
import { UseFormTrigger } from 'react-hook-form';
import { useAccount } from 'wagmi';

import { ProjectStageEnum } from 'src/global/enums/project.enum';
import { ProjectDetailType } from 'src/global/schemas/project.schema';
import { ProjectInputs } from 'src/global/types/project.type';
import { TProjectTheme } from 'src/global/types/theme.type';
import { useUpdateProjectMutation } from 'src/store/services/project.service';
import { useUploadFileToAwsMutation, useUploadToIpfsMutation } from 'src/store/services/upload.service';
import { convertFormDataToPayload } from 'src/utils/project';

interface UseSaveDraftResponse {
  isSaving: boolean;
  saveDraftAsync: (
    projectDetail: Partial<ProjectDetailType> & { id: string },
    draftData: ProjectInputs,
    theme: TProjectTheme,
    preCheck?: boolean,
  ) => Promise<ProjectDetailType | undefined>;
}

const cleanAllFields = (data: ProjectInputs) => {
  const cleanData = (obj: ProjectInputs): any => {
    if (Array.isArray(obj)) {
      // Just map through array elements without filtering
      return obj.map(cleanData);
    } else if (typeof obj === 'object' && obj !== null) {
      return Object.entries(obj).reduce((acc: any, [key, value]) => {
        const cleanedValue = cleanData(value);
        if (value instanceof Date) {
          acc[key] = value;
        } else if (cleanedValue !== undefined && cleanedValue !== null && cleanedValue !== '' && cleanedValue !== 0) {
          acc[key] = cleanedValue;
        }
        return acc;
      }, {});
    }
    return obj;
  };

  const cleanedData = cleanData(data);
  return cleanedData;
};

const withRetry = async <T>(fn: () => Promise<T>, retries = 3, baseDelay = 1000): Promise<T> => {
  let attempt = 0;

  const execute = async (currentDelay: number): Promise<T> => {
    try {
      // Add delay before every attempt including the first
      await new Promise(resolve => setTimeout(resolve, currentDelay));
      return await fn();
    } catch (error) {
      if (attempt >= retries) throw error;

      attempt++;
      const nextDelay = currentDelay * Math.pow(2, attempt);
      return execute(nextDelay);
    }
  };

  return execute(baseDelay);
};

export const useSaveDraft = (trigger?: UseFormTrigger<ProjectInputs>): UseSaveDraftResponse => {
  const [isSaving, setIsSaving] = useState(false);
  const { address } = useAccount();

  const [saveDraft] = useUpdateProjectMutation();
  const [uploadToAws] = useUploadFileToAwsMutation();
  const [uploadToIpfs] = useUploadToIpfsMutation();

  const collectPaths = (data: any, path = '', paths: string[] = []) => {
    if (typeof data === 'object' && data !== null) {
      Object.entries(data).forEach(([key, value]) => {
        const newPath = path ? `${path}.${key}` : key;
        collectPaths(value, newPath, paths);
      });
    } else {
      paths.push(path);
    }
    return paths;
  };

  const triggerAllFields = async (data: any) => {
    const paths = collectPaths(data);
    const isValid = await trigger?.(paths as Array<any>);
    if (!isValid) {
      throw new Error('Invalid form data');
    }
  };

  const saveDraftAsync = useCallback(
    async (
      projectDetail: Partial<ProjectDetailType> & { id: string },
      draftData: ProjectInputs,
      theme: TProjectTheme,
      preCheck?: boolean,
    ): Promise<ProjectDetailType | undefined> => {
      try {
        if (!address) return;
        const id = projectDetail.id;
        setIsSaving(true);
        const state = draftData.state;
        const cleanedData = cleanAllFields(draftData);
        if (trigger) {
          await triggerAllFields(cleanedData);
        }

        // check if an image is a new File that needs uploading
        // Files converted from AWS URLs have lastModified set to 2000-01-01, newly uploaded files have current timestamp
        const needsUpload = (image: any): image is File => {
          return image instanceof File && image.lastModified > new Date('2000-01-01').getTime();
        };

        const uploadTasks = {
          background: needsUpload(theme.theme.background.image)
            ? uploadToAws({
                imageType: 'background',
                file: theme.theme.background.image,
                filename: theme.theme.background.image.name,
                programId: id,
                address: address,
              })
            : null,
          logo: needsUpload(theme.image.logo)
            ? uploadToAws({
                imageType: 'logo',
                file: theme.image.logo,
                filename: theme.image.logo.name,
                programId: id,
                address: address,
              })
            : null,
          favicon: needsUpload(theme.image.favicon)
            ? uploadToAws({
                imageType: 'favicon',
                file: theme.image.favicon,
                filename: theme.image.favicon.name,
                programId: id,
                address: address,
              })
            : null,
          featureImage: needsUpload(theme.image.featureImage)
            ? uploadToAws({
                imageType: 'featureImage',
                file: theme.image.featureImage,
                filename: theme.image.featureImage.name,
                programId: id,
                address: address,
              })
            : null,
        };

        const filteredTasks = Object.fromEntries(Object.entries(uploadTasks).filter(([_, value]) => value !== null));
        const uploadResults: Record<string, any> = {};
        for (const [key, task] of Object.entries(filteredTasks)) {
          try {
            const result = await withRetry(() => task as Promise<any>, 3, 1000);
            uploadResults[key] = result?.data;
          } catch (error) {
            console.error(`Upload failed after retries for ${key}:`, error);
            throw new Error(`File upload failed: ${key}`);
          }
        }

        // Handle node asset upload (restore behavior from v3)
        let nodeAssetUrl: string | null = null;
        if (draftData?.node?.asset?.filter(asset => !!asset)?.length) {
          const nodeAsset = draftData.node.asset[0];
          if (needsUpload(nodeAsset)) {
            const result = await uploadToIpfs({
              ipfs: nodeAsset,
              projectId: id,
              address,
            });
            // prefer CDN gateway URL for getHashFromUrl compatibility
            nodeAssetUrl = (result as any)?.data?.cdn ?? null;
          } else {
            // Use existing URL if it's not a File object
            nodeAssetUrl = nodeAsset ? (projectDetail?.node?.asset as unknown as string) : null;
          }
        } else {
          // Fallback to existing node asset
          nodeAssetUrl = projectDetail?.node?.asset as unknown as string;
        }

        const formatData = convertFormDataToPayload(
          {
            ...cleanedData,
            node: {
              ...draftData.node,
              asset: nodeAssetUrl ? [nodeAssetUrl] : [],
            },
          },
          address,
          {
            ...theme,
            image: {
              ...theme.image,
              logo: uploadResults?.logo?.cdn || (theme.image.logo ? draftData?.theme?.image?.logo : null),
              favicon: uploadResults?.favicon?.cdn || (theme.image.favicon ? draftData?.theme?.image?.favicon : null),
              featureImage:
                uploadResults?.featureImage?.cdn ||
                (theme.image.featureImage ? draftData?.theme?.image?.featureImage : null),
            },
            theme: {
              ...theme.theme,
              background: {
                ...theme.theme.background,
                image:
                  uploadResults?.background?.cdn ||
                  (theme.theme.background.image ? draftData?.theme?.theme?.background?.image : null),
              },
            },
          },
        );

        if (state === ProjectStageEnum.PRODUCTION) {
          const data = await saveDraft({ id, data: formatData }).unwrap();
          if ('message' in data) {
            throw new Error((data.message as string) ?? 'Save failed');
          }
          return data;
        }

        const data = await saveDraft({ id, data: formatData, preCheck }).unwrap();
        if ('message' in data) {
          throw new Error((data.message as string) ?? 'Save Draft failed');
        }
        return data;
      } catch (error) {
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [address, trigger, triggerAllFields],
  );

  return {
    isSaving,
    saveDraftAsync,
  };
};
